import React, { Suspense } from "react";
import { Input, Text } from "@mantine/core";
import { useFormikContext } from "formik";
import "quill/dist/quill.snow.css";

/**
 * Pole treści z możliwością wstawiania odnośników.
 *
 * Osobny edytor, a nie ten z ofert i bloga: tamten rejestruje własne formaty
 * (`customFormatBlock`, `customFormatHeader-N`, `customFormatList`), które są
 * ostylowane wyłącznie w szablonach oferty i wpisu. Na stronach z zakładki
 * STRONY wyszłyby surowe, więc tutaj zostawiamy czysty HTML: akapit, pogrubienie,
 * kursywa, odnośnik i lista.
 *
 * Pasek celowo ubogi. To pole służy treści i linkowaniu wewnętrznemu, a nie
 * składaniu układu — rozmiary i nagłówki ustawia się znacznikiem przy polu.
 */

const TOOLBAR = [
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
  ["clean"],
];

const FORMATS = ["bold", "italic", "underline", "list", "link"];

const Editor = React.lazy(async () => {
  const rq = await import("react-quill-new");
  const ReactQuill = rq.default;

  const Komponent: React.FC<{ value: string; onChange: (v: string) => void }> = ({
    value,
    onChange,
  }) => (
    <ReactQuill
      theme="snow"
      // Quill 2 domyślnie oddaje getSemanticHTML(), które zamienia każdą spację
      // na &nbsp; i rozdyma zapis. Bierzemy root.innerHTML.
      useSemanticHTML={false}
      modules={{ toolbar: TOOLBAR }}
      formats={FORMATS}
      value={value}
      onChange={onChange}
    />
  );

  return { default: Komponent };
});

/** Pusty edytor oddaje `<p><br></p>`; dla reszty aplikacji to ma być pustka. */
export function normalizeRichText(html: string): string {
  const puste = html
    .replace(/<p>(\s|<br\s*\/?>|&nbsp;)*<\/p>/gi, "")
    .replace(/<br\s*\/?>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
  return puste === "" ? "" : html;
}

interface Props {
  /** Ścieżka pola w formularzu, np. "sections.intro.paragraphsBefore". */
  name: string;
  label: string;
  description?: string;
}

const RichTextField: React.FC<Props> = ({ name, label, description }) => {
  const { getFieldMeta, setFieldValue } = useFormikContext<any>();
  const wartosc = (getFieldMeta(name).value as string) ?? "";

  return (
    <Input.Wrapper label={label} description={description}>
      <Suspense fallback={<Text size="sm" c="dimmed">Ładowanie edytora…</Text>}>
        <Editor
          value={wartosc}
          onChange={(v) => setFieldValue(name, normalizeRichText(v))}
        />
      </Suspense>
    </Input.Wrapper>
  );
};

export default RichTextField;
